/**
 * Pure calendar utility functions extracted from ContractsBoard.
 *
 * These functions are framework-agnostic and depend only on date-fns.
 * They live here so they can be unit-tested without rendering the board.
 */

import { addDays, differenceInCalendarDays, isSameDay, startOfDay } from 'date-fns';
import type { Job } from '../app/data/mockData';

export type DayPosition = 'single' | 'start' | 'middle' | 'end';

/** Fixed pixel height for every job card slot on the board.
 *
 *  Phase 39-05 follow-up (2026-05-22): bumped 112 → 128 to accommodate the
 *  "Phase N of M" badge line above the title without crowding out the
 *  vehicle pills + staff avatars in the lower sections. Single-phase
 *  legacy cards (no badge) absorb the extra whitespace harmlessly.
 *
 *  #93/#102 follow-up (2026-06-09): bumped 128 → 152 (+24px = one avatar
 *  row worth: h-5 ≈ 20px + gap-0.5 ≈ 2px + breathing room). It is common to
 *  have >6 people on a job; the worker avatar row now spans up to TWO rows
 *  (see WORKER_LIMIT / the two-row max-height clamp in JobCard.tsx). Adding
 *  one row's height keeps the calendar grid aligned: empty slot placeholders
 *  use the same CARD_HEIGHT so columns never drift.
 */
export const CARD_HEIGHT = 152;

/**
 * Return all jobs whose date range spans the given calendar day (inclusive).
 * A job starting on 2026-04-01 with durationDays=3 spans 01, 02, 03.
 */
export function getJobsForDay(jobs: Job[], date: Date): Job[] {
  const day = startOfDay(date);
  return jobs.filter((job) => {
    const jobStart = startOfDay(job.date);
    const jobEnd = startOfDay(addDays(job.date, job.durationDays - 1));
    return day >= jobStart && day <= jobEnd;
  });
}

/**
 * Return the 1-based day number within a job's span for the given calendar day.
 * Day 1 = start date, day N = end date (where N = durationDays).
 * Callers should guard that date is within the job's range.
 */
export function getDayNumber(job: Job, date: Date): number {
  const jobStart = startOfDay(job.date);
  const day = startOfDay(date);
  return differenceInCalendarDays(day, jobStart) + 1;
}

/**
 * Determine where within a multi-day span the given day falls.
 *
 * Returns:
 *   'single' — job is only one day long
 *   'start'  — given day is the first day of a multi-day job
 *   'middle' — given day is neither first nor last
 *   'end'    — given day is the last day of a multi-day job
 */
export function getDayPosition(job: Job, date: Date): DayPosition {
  if (job.durationDays <= 1) return 'single';
  const day = startOfDay(date);
  const jobStart = startOfDay(job.date);
  const jobEnd = startOfDay(addDays(job.date, job.durationDays - 1));
  if (isSameDay(day, jobStart)) return 'start';
  if (isSameDay(day, jobEnd)) return 'end';
  return 'middle';
}

// ─── assignJobRows ────────────────────────────────────────────────────────────

export interface RowAssignment {
  /** Maps jobId → 0-based row index. */
  rowMap: Map<string, number>;
  /** Total number of rows needed (0 if no jobs). */
  rowCount: number;
}

/**
 * Assign each job a row index using a greedy interval-scheduling algorithm.
 *
 * Jobs are sorted by start date, then by duration descending (longer jobs
 * placed first for better packing). Each job is placed in the lowest-numbered
 * row where it doesn't overlap any already-placed job.
 *
 * Two jobs overlap if their date ranges share at least one calendar day:
 *   jobA.start <= jobB.end  AND  jobA.end >= jobB.start
 *
 * The result is stable within a given job set. Row assignments may shift
 * when new jobs load (e.g., lazy week loading).
 */
export function assignJobRows(jobs: Job[]): RowAssignment {
  if (jobs.length === 0) return { rowMap: new Map(), rowCount: 0 };

  const sorted = [...jobs].sort((a, b) => {
    const startDiff = startOfDay(a.date).getTime() - startOfDay(b.date).getTime();
    if (startDiff !== 0) return startDiff;
    const ionDiff = (parseInt(a.ion ?? '', 10) || 0) - (parseInt(b.ion ?? '', 10) || 0);
    if (ionDiff !== 0) return ionDiff;
    return b.durationDays - a.durationDays;
  });

  // rows[i] holds the jobs already placed in row i (for overlap checking)
  const rows: Job[][] = [];
  const rowMap = new Map<string, number>();

  for (const job of sorted) {
    const jobStart = startOfDay(job.date);
    const jobEnd = startOfDay(addDays(job.date, job.durationDays - 1));

    let placed = false;
    for (let r = 0; r < rows.length; r++) {
      const overlaps = rows[r].some((existing) => {
        const exStart = startOfDay(existing.date);
        const exEnd = startOfDay(addDays(existing.date, existing.durationDays - 1));
        return jobStart <= exEnd && jobEnd >= exStart;
      });
      if (!overlaps) {
        rows[r].push(job);
        rowMap.set(job.id, r);
        placed = true;
        break;
      }
    }

    if (!placed) {
      rows.push([job]);
      rowMap.set(job.id, rows.length - 1);
    }
  }

  return { rowMap, rowCount: rows.length };
}
